import Component from '@glimmer/component';
import { t } from 'ember-intl';
import SpaceCard from 'madow-tech/components/space-card';
import mult from 'madow-tech/helpers/mult';
import revealOnScroll from 'madow-tech/modifiers/reveal-on-scroll';

interface TestimonialItem {
  quoteKey: string;
  authorKey: string;
  contextKey: string;
  isFounderGuarantee?: boolean;
  linkKey?: string;
  linkUrl?: string;
}

export default class Testimonials extends Component {
  get items(): TestimonialItem[] {
    return [
      {
        quoteKey: 'testimonials.testimonial1.quote',
        authorKey: 'testimonials.testimonial1.author',
        contextKey: 'testimonials.testimonial1.context',
      },
      {
        quoteKey: 'testimonials.testimonial2.quote',
        authorKey: 'testimonials.testimonial2.author',
        contextKey: 'testimonials.testimonial2.context',
      },
      {
        quoteKey: 'testimonials.testimonial3.quote',
        authorKey: 'testimonials.testimonial3.author',
        contextKey: 'testimonials.testimonial3.context',
        isFounderGuarantee: true,
        linkKey: 'testimonials.testimonial3.link',
        linkUrl: 'https://marcemira.com/about',
      },
    ];
  }

  <template>
    <section id="testimonials" class="py-20 md:py-28">
      <div class="mx-auto max-w-7xl px-4">
        <div class="mb-12 md:mb-16">
          <h2
            class="text-3xl md:text-4xl font-bold mb-4"
            {{revealOnScroll animation="fade-up"}}
          >
            {{t "testimonials.title"}}
          </h2>
          <p
            class="text-zinc-400 text-base md:text-lg max-w-2xl"
            {{revealOnScroll animation="fade-up" delay=100}}
          >
            {{t "testimonials.subtitle"}}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
          {{#each this.items as |item idx|}}
            <SpaceCard
              @variant="v2"
              @bodyClass='!h-full'
              @hasHoverEffect={{true}}

              {{revealOnScroll animation="fade-up" delay=(mult idx 100)}}
            >
              <blockquote class="flex flex-col h-full">
                {{! Context badge - different style for founder guarantee }}
                {{#if item.isFounderGuarantee}}
                  <span class="inline-block self-start text-xs font-medium px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 mb-4">
                    {{t item.contextKey}}
                  </span>
                {{else}}
                  <span class="inline-block self-start text-xs font-medium px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 mb-4">
                    {{t item.contextKey}}
                  </span>
                {{/if}}

                {{! Quote }}
                <p class="text-sm text-zinc-200 flex-grow leading-relaxed">
                  {{#if item.isFounderGuarantee}}
                    {{t item.quoteKey}}
                  {{else}}
                    "{{t item.quoteKey}}"
                  {{/if}}
                </p>

                {{! Author }}
                <footer class="mt-6 pt-4 border-t border-zinc-700/50">
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                        {{#if item.isFounderGuarantee}}
                          <span class="text-lg">🛡️</span>
                        {{else}}
                          <span class="text-lg">💬</span>
                        {{/if}}
                      </div>
                      <span class="text-sm text-zinc-300 font-medium">
                        {{t item.authorKey}}
                      </span>
                    </div>
                    {{#if item.isFounderGuarantee}}
                      <a
                        href={{item.linkUrl}}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        {{t item.linkKey}}
                      </a>
                    {{/if}}
                  </div>
                </footer>
              </blockquote>
            </SpaceCard>
          {{/each}}
        </div>

        {{! CTA for references }}
        <div
          class="mt-12 text-center"
          {{revealOnScroll animation="fade-up" delay=400}}
        >
          <p class="text-zinc-400 text-sm mb-4">
            {{t "testimonials.cta"}}
          </p>
          <a
            href="#contact"
            class="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-medium"
          >
            Request References
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  </template>
}
